<script setup lang="ts">
/**
 * Skip links: the first tab stops of a page, invisible until focused, that jump
 * straight to its landmarks („Zur Werkzeugleiste“, „Zum Inhalt“). Following a
 * link moves the focus to the target too — a target that is not focusable by
 * itself gets `tabindex="-1"` for that — so keyboard and screen-reader users
 * really land there instead of only scrolling.
 *
 * Put it first in the page; it positions itself at the top-start corner.
 */
export interface NxSkipLink {
  /** Id of the target element, without `#`. */
  target: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    links: readonly NxSkipLink[];
    /** Accessible name of the nav. */
    ariaLabel?: string;
  }>(),
  { ariaLabel: 'Sprunglinks' }
);

function jump(event: MouseEvent, target: string) {
  const el = typeof document !== 'undefined' ? document.getElementById(target) : null;
  if (!el) return;
  event.preventDefault();
  if (!el.hasAttribute('tabindex') && !isFocusable(el)) el.setAttribute('tabindex', '-1');
  el.focus();
  el.scrollIntoView?.({ block: 'start' });
}

function isFocusable(el: HTMLElement): boolean {
  return /^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName) || el.isContentEditable;
}
</script>

<template>
  <nav class="nx-skip-links" :aria-label="props.ariaLabel">
    <a
      v-for="link in props.links"
      :key="link.target"
      class="nx-skip-links__link"
      :href="`#${link.target}`"
      data-testid="nx-skip-link"
      @click="jump($event, link.target)"
    >
      {{ link.label }}
    </a>
  </nav>
</template>

<style scoped>
.nx-skip-links {
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  z-index: var(--nxt-z-tooltip);
}
/* Visually hidden until focused (kept in the tab order). */
.nx-skip-links__link {
  position: absolute;
  inset-block-start: var(--nxt-space-xs);
  inset-inline-start: var(--nxt-space-xs);
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
.nx-skip-links__link:focus,
.nx-skip-links__link:focus-visible {
  width: auto;
  height: auto;
  padding: var(--nxt-space-xs) var(--nxt-space-md);
  overflow: visible;
  clip-path: none;
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-brand-bg);
  box-shadow:
    0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg),
    0 0 0 calc(var(--nxt-hairline) * 4) var(--nxt-focus-ring),
    var(--nxt-shadow-md);
  color: var(--nxt-brand-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  text-decoration: none;
  outline: none;
}
</style>
